import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Btn, Empty, Badge } from "../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea, SelectWithCustom } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { TOP_US_INSURERS, SERVICES } from "./engagement/constants";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

const FREQUENCIES = ["1x/week", "2x/week", "Bi-weekly", "Monthly", "One-time", "As needed"];
const STATUSES = ["pending", "active", "denied"];
const EMPTY = { auth_number: "", company: "", member_id: "", service: "", cpt_codes: "", sessions_approved: "", sessions_used: "0", frequency: "1x/week", start_date: "", expires: "", status: "active", notes: "", requested_by: "" };

function UsageBar({ used, approved }) {
  const u = Number(used) || 0;
  const a = Number(approved) || 0;
  const pct = a ? Math.min(100, Math.round((u / a) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs text-[#7B6157] mb-1"><span>{u}/{a} sessions used</span><span>{pct}%</span></div>
      <div className="h-2 rounded-full bg-[#EFEBE0] overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 100 ? "bg-[#C62828]" : pct >= 80 ? "bg-[#E65100]" : "bg-[#2E7D32]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function AuthorizationsPanel({ client }) {
  const { user } = useAuth();
  const editable = canEdit(user, "billing");
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/authorizations", { params: { client_id: client.id } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/authorizations/${editId}`, form);
      else await api.post("/authorizations", { ...form, client_id: client.id });
      toast.success(editId ? "Authorization updated" : "Authorization added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/authorizations/${confirmDelete.id}`);
      toast.success("Authorization deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="authorizations-panel">
      <div className="flex items-center justify-between mb-4">
        {!editable ? <ReadOnlyChip /> : <span />}
        {editable && <Btn data-testid="add-auth-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(EMPTY); setEditId(null); setPanel(true); }}><Plus size={14} /> Add Authorization</Btn>}
      </div>
      {items.length === 0 ? <Empty text="No authorizations on file" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`auth-card-${a.id}`} className="rounded-2xl bg-[#F9F5EE] border border-[#F1E8DA] p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-heading font-bold text-[#3E2723]"><ShieldCheck size={15} className="text-[#7B6157]" /> {a.auth_number}</div>
                  <div className="text-sm text-[#6D4C41] mt-0.5 break-words">{a.company}</div>
                </div>
                <Badge value={a.status} />
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#7B6157]">
                {a.service && <span>{a.service}</span>}
                {a.cpt_codes && <span>CPT {a.cpt_codes}</span>}
                {a.frequency && <span>{a.frequency}</span>}
                {a.member_id && <span>{a.member_id}</span>}
              </div>
              <div className="mt-3"><UsageBar used={a.sessions_used} approved={a.sessions_approved} /></div>
              <div className="mt-2.5 flex items-end justify-between gap-2">
                <div className="text-xs text-[#7B6157]">
                  {a.start_date && <span>From {dayjs(a.start_date).format("MM/DD/YYYY")} · </span>}
                  {a.expires && <span className={a.status === "expiring" || a.status === "expired" ? "font-semibold text-[#C62828]" : ""}>Expires {dayjs(a.expires).format("MM/DD/YYYY")}</span>}
                </div>
                {editable && (
                  <div className="flex gap-1.5 shrink-0">
                    <button type="button" data-testid={`edit-auth-${a.id}`} aria-label="Edit" onClick={() => { setForm({ ...EMPTY, ...a, sessions_approved: String(a.sessions_approved ?? ""), sessions_used: String(a.sessions_used ?? "0") }); setEditId(a.id); setPanel(true); }}
                      className="h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                    <button type="button" data-testid={`delete-auth-${a.id}`} aria-label="Delete" onClick={() => setConfirmDelete(a)}
                      className="h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
              {a.notes && <p className="mt-2 text-xs text-[#7B6157] bg-white/60 rounded-lg px-2.5 py-1.5">{a.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Authorization" : "Add Authorization"}
        subtitle={`${client.first_name} ${client.last_name} - insurance authorization`} testId="auth-slide-panel">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Authorization # *</FLabel><FInput data-testid="auth-number-input" value={form.auth_number} onChange={f("auth_number")} placeholder="AUTH-00000" /></div>
            <div><FLabel>Member ID</FLabel><FInput data-testid="auth-member-input" value={form.member_id} onChange={f("member_id")} /></div>
            <div className="col-span-2"><FLabel>Insurance Company *</FLabel>
              <SelectWithCustom testId="auth-company-input" value={form.company} onChange={(v) => setForm((s) => ({ ...s, company: v }))} options={TOP_US_INSURERS} customPlaceholder="Enter insurance company" /></div>
            <div><FLabel>Service</FLabel>
              <SelectWithCustom testId="auth-service-input" value={form.service} onChange={(v) => setForm((s) => ({ ...s, service: v }))} options={SERVICES} customPlaceholder="Enter service" /></div>
            <div><FLabel>CPT Code(s)</FLabel><FInput data-testid="auth-cpt-input" value={form.cpt_codes} onChange={f("cpt_codes")} placeholder="90837, 90853" /></div>
            <div><FLabel>Sessions Approved</FLabel><FInput data-testid="auth-approved-input" type="number" min="0" value={form.sessions_approved} onChange={f("sessions_approved")} /></div>
            <div><FLabel>Sessions Used</FLabel><FInput data-testid="auth-used-input" type="number" min="0" value={form.sessions_used} onChange={f("sessions_used")} /></div>
            <div><FLabel>Frequency</FLabel><FSelect data-testid="auth-frequency-input" value={form.frequency} onChange={f("frequency")}>{FREQUENCIES.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Status</FLabel>
              <FSelect data-testid="auth-status-input" value={["pending", "denied"].includes(form.status) ? form.status : "active"} onChange={f("status")}>
                {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}{s === "active" ? " (auto: expiring/expired/exhausted)" : ""}</option>)}
              </FSelect></div>
            <div><FLabel>Start Date</FLabel><DateField testId="auth-start-input" value={form.start_date} onChange={(v) => setForm((s) => ({ ...s, start_date: v }))} /></div>
            <div><FLabel>Expiration Date</FLabel><DateField testId="auth-expires-input" value={form.expires} onChange={(v) => setForm((s) => ({ ...s, expires: v }))} /></div>
            <div className="col-span-2"><FLabel>Requested By</FLabel><FInput value={form.requested_by} onChange={f("requested_by")} /></div>
          </div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="auth-notes-input" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="auth-save-btn" type="submit" disabled={busy || !form.auth_number.trim() || !form.company.trim()}>{busy ? "Saving…" : editId ? "Save Changes" : "Add Authorization"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={doDelete} busy={busy}
        title="Delete authorization"
        description={`Delete ${confirmDelete?.auth_number} (${confirmDelete?.company}) for ${client.first_name} ${client.last_name}? Billing claims may reference this authorization.`} />
    </div>
  );
}
